<script setup>
// Reference 5 — where the money stays, and who says that is the right arrangement.
//
// The permissions ledger on the left is not a new claim: it is the second half of the
// paragraph above it, tabulated. "Pandig vraagt alleen de rechten om machtigingen en
// incasso's klaar te zetten en hun status te lezen; uitbetalingen en saldo vallen
// daarbuiten" is exactly a three-yes/two-no list, and a manager checking what a tool
// may touch reads a list where they would skim a sentence.
//
// The two authority paragraphs become two bordered cards headed by their source. Same
// words, same links — but a citation reads as a citation, which is the job it is doing
// on a page that asks someone to trust software with other people's money.
import { ref, onMounted, onUnmounted } from "vue";
import { Check, X } from "@lucide/vue";
import { useMotion, reveal } from "@/lib/motion";

const permissions = [
  { allowed: true, label: "Machtigingen klaarzetten" },
  { allowed: true, label: "Incasso's klaarzetten" },
  { allowed: true, label: "De status ervan lezen" },
  { allowed: false, label: "Uitbetalingen" },
  { allowed: false, label: "Saldo" },
];

const root = ref(null);
const blocks = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    reveal(blocks.value, { stagger: 0.1, y: 16, trigger: root.value, start: "top 80%" });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section id="geld" ref="root" class="border-t-2 border-foreground bg-background">
    <div class="mx-auto w-full max-w-6xl px-5 py-10 sm:py-16">
      <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
        Wat bij de VvE blijft, en bij jou.
      </h2>

      <div class="mt-6 grid gap-6 sm:mt-10 lg:grid-cols-2 lg:gap-12">
        <div :ref="(el) => (blocks[0] = el)">
          <p class="text-sm leading-relaxed text-muted-foreground sm:text-base">
            Pandig raakt het geld niet aan. De bijdragen gaan van de appartementseigenaar rechtstreeks naar de eigen rekening van de VvE, via een
            Mollie-account op naam van de VvE. Pandig vraagt alleen de rechten om machtigingen en incasso's klaar te zetten en hun status te lezen;
            uitbetalingen en saldo vallen daarbuiten.
          </p>

          <ul class="mt-5 border-2 border-foreground bg-card shadow-[var(--shadow-hard)]">
            <li
              v-for="permission in permissions"
              :key="permission.label"
              class="flex items-center gap-3 border-b-2 border-foreground/10 px-3 py-2.5 last:border-b-0 sm:px-5 sm:py-3"
            >
              <span
                class="flex size-5 shrink-0 items-center justify-center border-2 border-foreground sm:size-6"
                :class="permission.allowed ? 'bg-secondary text-success' : 'bg-card text-muted-foreground'"
              >
                <component :is="permission.allowed ? Check : X" class="size-3 sm:size-3.5" />
              </span>
              <span
                class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] sm:text-[0.6875rem]"
                :class="permission.allowed ? 'text-foreground' : 'text-muted-foreground line-through'"
              >
                {{ permission.label }}
              </span>
            </li>
          </ul>
        </div>

        <div class="grid gap-4 sm:gap-6">
          <div :ref="(el) => (blocks[1] = el)" class="border-2 border-foreground bg-card p-4 shadow-[var(--shadow-hard)] sm:p-6">
            <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">Vereniging Eigen Huis</p>
            <p class="mt-2 text-sm leading-relaxed text-muted-foreground sm:mt-3 sm:text-base">
              Dat past bij het advies om als VvE de regie te houden, ook wanneer het beheer is uitbesteed.
              <a
                href="https://www.eigenhuis.nl/vve/beheer/stappenplan--vve-beheer-in-eigen-hand-houden"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold text-primary underline-offset-4 hover:underline"
                >Vereniging Eigen Huis</a
              >
              adviseert toezicht bij het bestuur, vier ogen op grotere betalingen en geen zelfstandige bevoegdheid van de beheerder over het
              reservefonds. Het bestuur blijft zelf
              <a
                href="https://www.eigenhuis.nl/vve/bestuur-vve"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold text-primary underline-offset-4 hover:underline"
                >verantwoordelijk voor het innen van de bijdragen en de administratie</a
              >.
            </p>
          </div>

          <div :ref="(el) => (blocks[2] = el)" class="border-2 border-foreground bg-card p-4 shadow-[var(--shadow-hard)] sm:p-6">
            <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">
              Landelijke standaard voor professioneel VvE-beheer, 2026
            </p>
            <p class="mt-2 text-sm leading-relaxed text-muted-foreground sm:mt-3 sm:text-base">
              Het sluit ook aan bij de
              <a
                href="https://www.volkshuisvestingnederland.nl/documenten/2026/01/09/nieuwe-standaard-voor-vve-beheer"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold text-primary underline-offset-4 hover:underline"
                >landelijke standaard voor professioneel VvE-beheer</a
              >
              uit 2026: duidelijke afspraken over taken, kosten en verantwoordelijkheid, rechtmatig en transparant financieel beheer, en voorspelbare
              communicatie. Dat is eenvoudiger te verantwoorden als de rekening en de machtigingen van de VvE zelf zijn.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
